import { FormSubmitType } from '@app/config';
import {
    EocClient,
    ClientType,
    ExchangeOfContracts,
    TemplatePlaceholderKeys,
    EocDocumentCategory
} from '@app/types/ExchangeOfContracts';
import {
    CustomPlaceholder,
    CustomPlaceholderRespondentType,
    isUploadedDocument,
    LocalDocument,
    UploadedDocument
} from '@app/types/UploadADoc';
import { FormApi } from 'final-form';
import * as yup from 'yup';
import { IntegrationSearchResult, VaultReIntegrationSearchResult } from './integration/IntegrationAddressSection';
import { integration, integrationType } from '@app/constants/constants';
import { FormValues } from './eoc-create/EocCreate';
import { UploadedDocumentState } from '@app/containers/PdfViewer/components/useUploadedDocumentState';
import { doesDocumentHaveSignaturesForAllClients } from '@app/containers/PdfViewer/utils';

export type FormClient = Pick<EocClient, 'name' | 'email' | 'clientType' | 'phone'> & {
    id?: string;
};

export type FormModel = {
    address: string;
    purchasers: FormClient[];
    vendors: FormClient[];
    customPlaceholders?: CustomPlaceholder[];
    submitType?: FormSubmitType;
    descriptionText: string;
    hasReviewedContract?: boolean;
};

export const projectFormModel = (data: ExchangeOfContracts): FormModel => {
    const purchasers = data.client?.clients?.filter(client => client.clientType === ClientType.PURCHASER);
    const vendors = data.client?.clients?.filter(client => client.clientType === ClientType.VENDOR);

    return {
        purchasers: purchasers?.length
            ? purchasers
            : [{ name: '', email: '', phone: '', clientType: ClientType.PURCHASER }],
        vendors: vendors?.length ? vendors : [{ name: '', email: '', phone: '', clientType: ClientType.VENDOR }],
        customPlaceholders: data.customPlaceholders || [],
        address: data.address,
        descriptionText: data.descriptionText,
        hasReviewedContract: data.hasReviewedContract
    };
};

type ApiClient = FormClient & { signingOrder: number; _id?: string };

export type ApiModel = {
    clients: ApiClient[];
    uploadedDocuments: (UploadedDocument | LocalDocument)[];
    customPlaceholders: CustomPlaceholder[];
    address: string;
    descriptionText: string;
    hasReviewedContract?: boolean;
};

export const projectApiModelWithoutUploadedDocuments = (formModel: FormModel): Omit<ApiModel, 'uploadedDocuments'> => {
    const clients = [...formModel.purchasers, ...formModel.vendors].map((client, index) => {
        return {
            ...client,
            name: client.name || '',
            email: client.email || '',
            phone: client.phone || '',
            signingOrder: index + 1,
            ...(client.id ? { _id: client.id } : {})
        };
    });

    return {
        clients,
        customPlaceholders: formModel.customPlaceholders || [],
        address: formModel.address,
        descriptionText: formModel.descriptionText,
        hasReviewedContract: formModel.hasReviewedContract || false
    };
};

export const projectApiModel = (formModel: FormModel, uploadedDocuments: ApiModel['uploadedDocuments']): ApiModel => {
    return {
        ...projectApiModelWithoutUploadedDocuments(formModel),
        uploadedDocuments
    };
};

type ClientError = {
    name?: string;
    email?: string;
    phone?: string;
};

type FormErrors = {
    purchasers?: ClientError[];
    vendors?: ClientError[];
    customPlaceholders?: {
        label?: string;
        value?: string;
        count?: string;
    }[];
};

type ApiErrors = {
    client?: { clients?: ClientError[] };
    customPlaceholders?: {
        label?: string;
        value?: string;
        count?: string;
    }[];
};

// TODO: Uploaded documents errors?
export const projectFormErrors = (errors: ApiErrors, values: FormModel): FormErrors => {
    const purchasers = errors.client?.clients?.slice(0, values.purchasers.length);
    const vendors = errors.client?.clients?.slice(values.purchasers.length);
    const e = {
        purchasers,
        vendors,
        customPlaceholders: errors.customPlaceholders
    };

    return e;
};

export const getMaxRecipients = (clientType: ClientType, formValues: FormModel): number => {
    const MAX_TOTAL_RECIPIENTS = 8;
    const MAX_SECTION_RECIPIENTS = 7;

    if (clientType === ClientType.PURCHASER) {
        return Math.min(MAX_SECTION_RECIPIENTS, MAX_TOTAL_RECIPIENTS - formValues.vendors.length);
    } else {
        return Math.min(MAX_SECTION_RECIPIENTS, MAX_TOTAL_RECIPIENTS - formValues.purchasers.length);
    }
};

const UNREVIEWED_DOCUMENT_ERROR = 'You must review the document before sending.';
export const formValuesSchema = yup.object().shape({
    purchasers: yup
        .array(
            yup.object().shape({
                name: yup.string().required('Name is required'),
                email: yup.string().email('Invalid email').required('Email is required'),
                phone: yup.string().required('Phone is required'),
                clientType: yup.string().oneOf([ClientType.PURCHASER]).required(),
                id: yup.string().notRequired()
            })
        )
        .min(1, 'At least one purchaser is required'),
    vendors: yup
        .array(
            yup.object().shape({
                name: yup.string().required('Name is required'),
                email: yup.string().email('Invalid email').required('Email is required'),
                phone: yup.string().required('Phone is required'),
                clientType: yup.string().oneOf([ClientType.VENDOR]).required(),
                id: yup.string().notRequired()
            })
        )
        .min(1, 'At least one vendor is required'),
    customPlaceholders: yup.array(
        yup.object().shape({
            id: yup.string().required(),
            label: yup.string().required('Label is required'),
            value: yup.string().when('respondentType', {
                is: CustomPlaceholderRespondentType.SENDER,
                then: schema => schema.required('Value is required'),
                otherwise: schema => schema.notRequired()
            }),
            respondentType: yup
                .string()
                .oneOf([CustomPlaceholderRespondentType.SENDER, CustomPlaceholderRespondentType.CLIENT])
                .required(),
            count: yup.number().required().min(1, 'Placeholder is missing from the document')
        })
    ),
    uploadedDocuments: yup
        .array(
            yup.object().shape({
                document: yup
                    .object()
                    .shape({
                        documentName: yup.string().required(),
                        fileName: yup.string().required(),
                        file: yup.string().required(),
                        size: yup.number().required(),
                        category: yup.string().required(),
                        leaseType: yup.string().required()
                    })
                    .required(),
                documentType: yup.string().optional()
            })
        )
        .required()
        .test('has-sales-contract', 'A Sales Contract document is required', uploadedDocuments => {
            return uploadedDocuments?.some(doc => doc.documentType === EocDocumentCategory.SALES_CONTRACT) || false;
        }),
    hasReviewedContract: yup.boolean().required(UNREVIEWED_DOCUMENT_ERROR).isTrue(UNREVIEWED_DOCUMENT_ERROR),
    doAllRecipientsHaveSignatures: yup.boolean().test(
        (doAllRecipientsHaveSignatures, { createError }) =>
            doAllRecipientsHaveSignatures ||
            createError({
                message: 'The Sales Contract and Authority to Exchange documents must have signatures for all clients.'
            })
    )
});

const recipientsSchema = yup.object().shape({
    purchasers: formValuesSchema.fields.purchasers,
    vendors: formValuesSchema.fields.vendors
});

export const isRecipientSectionValid = (values: FormModel): boolean => {
    try {
        recipientsSchema.validateSync(values, { abortEarly: false });
        return true;
    } catch (error) {
        return false;
    }
};

export const doAllRecipientsHaveNames = (values: FormModel): boolean => {
    return values.purchasers.every(client => !!client.name) && values.vendors.every(client => !!client.name);
};

export const getCustomPlaceholderValueField = (key: string, form: FormApi<FormModel>) => {
    const values = form.getState().values;
    const index = values.customPlaceholders?.findIndex(cp => cp.key === key);
    const fieldName = `customPlaceholders[${index}].value`;
    if (index !== undefined && index !== -1 && form.getRegisteredFields().find(f => f === fieldName)) {
        return form.getFieldState(fieldName as keyof FormModel);
    }
};

export const getCombinedClientNames = (clients: FormModel['purchasers'] | FormModel['vendors']): string => {
    return clients.map(client => client.name).join(', ');
};

export const handleClientNamesChange = (form: FormApi<FormModel>, clientType: 'purchasers' | 'vendors') => {
    const values = form.getState().values;
    const customPlaceholderKey =
        clientType === 'purchasers' ? TemplatePlaceholderKeys.PURCHASER_NAMES : TemplatePlaceholderKeys.VENDOR_NAMES;
    const clientNameField = getCustomPlaceholderValueField(customPlaceholderKey, form);

    if (!clientNameField) {
        return;
    }

    const combinedNames = getCombinedClientNames(values[clientType]);

    if (clientNameField.value === combinedNames) {
        return;
    }

    clientNameField.change(combinedNames);
};

export const handleSelectIntegration = (
    form: FormApi<FormValues, Partial<FormValues>>,
    selectedIntegration: IntegrationSearchResult
) => {
    if (selectedIntegration) {
        let address = '';
        let integrationPropertyId = '';
        if (selectedIntegration.integration === integration.INTEGRATION_VAULT_RE) {
            selectedIntegration = selectedIntegration as VaultReIntegrationSearchResult;
            address = selectedIntegration.addressText || selectedIntegration.fullName || '';
            integrationPropertyId = selectedIntegration.id;
        }

        form.batch(() => {
            form.change('integration.address', address);
            form.change('integration.id', integrationPropertyId);
            form.change('integration.type', selectedIntegration?.integration);

            if (selectedIntegration?.integration === integration.INTEGRATION_VAULT_RE) {
                selectedIntegration = selectedIntegration as VaultReIntegrationSearchResult;
                form.change('integration.entityType', selectedIntegration.integrationType);
                if (selectedIntegration.integrationType === integrationType.PROPERTY) {
                    form.change('integration.vaultReLifeId', selectedIntegration.lifeId);
                    form.change('integration.vaultReLifeType', selectedIntegration.lifeType);
                }
            } else {
                form.change('integration.entityType', integrationType.PROPERTY);
            }
            form.change('address', address);
        });
    }
};

export const resetIntegrationFormData = (form: FormApi<FormValues, Partial<FormValues>>) => {
    form.batch(() => {
        form.change('address', undefined);
        form.change('integration', {});
    });
};
export const getEocDocumentTypeDisplayName = (category?: string): string | undefined => {
    switch (category) {
        case EocDocumentCategory.SALES_CONTRACT:
            return 'Sales Contract';
        case EocDocumentCategory.AUTHORITY_TO_EXCHANGE:
            return 'Authority to Exchange';
        default:
            return undefined;
    }
};

export const isCurrencyPlaceholder = (key?: TemplatePlaceholderKeys) => {
    return (
        key === TemplatePlaceholderKeys.OFFER_AMOUNT ||
        key === TemplatePlaceholderKeys.OFFER_DEPOSIT_AMOUNT ||
        key === TemplatePlaceholderKeys.BALANCE_PAYABLE
    );
};

export const doRequiredDocumentsHaveSignatures = (
    numClients: number,
    uploadedDocumentList: (UploadedDocument | LocalDocument)[],
    uploadedDocumentStateItems: UploadedDocumentState[]
) => {
    const requiredDocuments = uploadedDocumentList.filter(
        document =>
            document.documentType === EocDocumentCategory.SALES_CONTRACT ||
            document.documentType === EocDocumentCategory.AUTHORITY_TO_EXCHANGE
    );
    return requiredDocuments.every(document => {
        if (!isUploadedDocument(document)) {
            return false;
        }
        const uploadedDocumentStateItem = uploadedDocumentStateItems.find(
            item => item.uploadedDocumentId === document.id
        );

        return (
            uploadedDocumentStateItem && doesDocumentHaveSignaturesForAllClients(numClients, uploadedDocumentStateItem)
        );
    });
};
